/**
 * Dichtheid Ruim of Compact (ronde 3, 28-10-2026).
 *
 * De gebruiker kiest in het gebruikersmenu. De keuze wordt per gebruiker
 * onthouden in `localStorage` onder `atlas.density:<e-mail>`. Zonder keuze is
 * het Compact onder 1440 px breed en anders Ruim; die standaard volgt het
 * venster, ook als je het tijdens het werken verkleint.
 *
 * De schil zet de uitkomst als `data-density` op `<html>`. Tokens in
 * tokens.css (`--atlas-row-height`, `--atlas-card-pad`, `--atlas-table-font`,
 * `--atlas-gap`, `--atlas-kpi-*`) krijgen daar hun compacte waarde; schermen
 * gebruiken die tokens en geen eigen vaste maten.
 */
import { COMPACT_BELOW } from "./breakpoints";

export type Density = "ruim" | "compact";

const PREFIX = "atlas.density:";

/** De sleutel in localStorage voor deze gebruiker. */
export const densityKey = (user: string) => `${PREFIX}${user.trim().toLowerCase()}`;

/** De standaard als de gebruiker zelf niets koos. */
export const defaultDensity = (width: number): Density => (width < COMPACT_BELOW ? "compact" : "ruim");

/** Alleen een geldige waarde telt; al het andere is "geen keuze". */
export const parseDensity = (value: string | null | undefined): Density | null =>
  value === "ruim" || value === "compact" ? value : null;

/** De dichtheid die geldt: de eigen keuze, anders de standaard bij deze breedte. */
export const resolveDensity = (stored: Density | null, width: number): Density =>
  stored ?? defaultDensity(width);

/** De keuze lezen; zonder opslag (privévenster, geblokkeerd) is er geen keuze. */
export function readDensity(user: string | null | undefined, storage: Pick<Storage, "getItem"> | null = safeStorage()) {
  if (!user || !storage) return null;
  try {
    return parseDensity(storage.getItem(densityKey(user)));
  } catch {
    return null;
  }
}

/** De keuze bewaren. Lukt dat niet, dan geldt hij alleen deze sessie. */
export function writeDensity(
  user: string | null | undefined,
  density: Density,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
) {
  if (!user || !storage) return;
  try {
    storage.setItem(densityKey(user), density);
  } catch {
    // Opslag vol of geweigerd: de keuze blijft in het geheugen van de schil.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
